/**
 * Wave 3 · B7 — localStorage persistence for the open-tabs list. Mirrors the
 * pattern in lib/tableLayout.tsx (namespaced + versioned key, always-on
 * localStorage backing, best-effort try/catch, safe-parse with validation so
 * a stale/foreign payload from an old build — or a logout/login cycle that
 * changes what routes are valid — never crashes the shell; it just falls
 * back to an empty tab list).
 */
import type { TabRecord } from './types'
import { ADS_HUB_CUTOVER } from '../pages/ads/legacyRoutes'
import { migrateLegacyAdsTabs } from './migrateAdsTabs'

const STORAGE_KEY = 'clickpay.tabs.v1'

interface StoredShape {
  tabs: TabRecord[]
}

function isValidTab(x: unknown): x is TabRecord {
  if (!x || typeof x !== 'object') return false
  const t = x as Record<string, unknown>
  if (typeof t.id !== 'string' || !t.id) return false
  if (typeof t.title !== 'string') return false
  if (typeof t.pinned !== 'boolean') return false
  if (typeof t.lastActiveAt !== 'number') return false
  const loc = t.location as Record<string, unknown> | undefined
  if (!loc || typeof loc.pathname !== 'string' || !loc.pathname.startsWith('/')) return false
  if (typeof loc.search !== 'string' || typeof loc.hash !== 'string') return false
  return true
}

export function loadTabs(): TabRecord[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as Partial<StoredShape>
    if (!parsed || !Array.isArray(parsed.tabs)) return []
    const tabs = parsed.tabs.filter(isValidTab)
    // PR 5: old PPC pills become one Advertising tab (saved back on the first save).
    return ADS_HUB_CUTOVER ? migrateLegacyAdsTabs(tabs) : tabs
  } catch {
    return []
  }
}

export function saveTabs(tabs: TabRecord[]): void {
  if (typeof window === 'undefined') return
  try {
    const payload: StoredShape = { tabs }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // quota / private-mode / unavailable — best-effort, same as tableLayout.
  }
}
